import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getJournals, getExecutiveEditors, getVerboseIssues, sortArticlesBySequence } from '../services/firebaseService';
import { formatIssueRecord } from '../utils/issueFormat';
import assetService from '../services/assetService';
import {
  FaArrowRight,
  FaBriefcase,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import ajhmBanner from '../assets/img/ajhm-banner.png';
import { usePageContent } from '../services/pageContentService';
import IssueArticleGroups from '../components/IssueArticleGroups';

// Card body HTML (from Admin -> Live Page Contents -> Home Page Cards)
const CARD_BODY_CLASS =
  'text-sm text-default leading-relaxed space-y-2 [&_strong]:font-semibold [&_strong]:text-gray-700 [&_a]:text-[#03478E] [&_a:hover]:underline [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4';

const Home = () => {
  const [journals, setJournals] = useState([]);
  const [editors, setEditors] = useState([]);
  const { content: liveCards, loading: cardsLoading } = usePageContent('home_cards');
  // Card text appears once the saved text has loaded (no flash of the built-in default)
  const cards = cardsLoading ? {} : liveCards;
  const [currentIssue, setCurrentIssue] = useState({ Vol: '1', issue: '1', validity: 'January to June 2026' });

  useEffect(() => {
    getVerboseIssues()
      .then((list) => {
        const current = list.find((i) => i.isCurrentIssue) || list[0];
        if (current) setCurrentIssue(current);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    // 1. Load Journals: show cached list immediately, then always refresh so admin edits appear
    const cachedJournals = assetService.getCache('journals');
    if (cachedJournals) {
      setJournals(sortArticlesBySequence(cachedJournals));
    }
    getJournals().then((data) => {
      const sorted = sortArticlesBySequence(data || []);
      setJournals(sorted);
      assetService.setCache('journals', sorted);
    });

    // 2. Load Executive Editors: show cached list immediately, then always refresh so admin edits appear
    const cachedEditors = assetService.getCache('editors');
    if (cachedEditors) {
      setEditors(cachedEditors);
    }
    getExecutiveEditors().then((data) => {
      setEditors(data || []);
      assetService.setCache('editors', data);
    });

  }, []);

  // Current issue journals list sorted by sequence
  const currentIssueJournals = useMemo(() => {
    const filtered = journals.filter((j) => j.isCurrentIssue);
    const list = filtered.length > 0 ? filtered : journals.slice(0, 10);
    return sortArticlesBySequence(list);
  }, [journals]);

  // Executive Editors in the order set in Admin -> Executive Editors (sequence number),
  // Dr. Dheeraj always first, the same as the admin list
  const sortedExecutiveEditors = useMemo(() => {
    const isDheeraj = (e) => (e.name || '').toLowerCase().includes('dheeraj');
    const seq = (e) => {
      const n = parseInt(e.sequenceNumber, 10);
      return Number.isNaN(n) ? Infinity : n;
    };
    return [...editors].sort((a, b) => {
      if (isDheeraj(a) !== isDheeraj(b)) return isDheeraj(a) ? -1 : 1;
      return seq(a) - seq(b);
    });
  }, [editors]);

  // Card Carousel state and grouping (3 cards per slide across full width)
  const [activeEditorSlide, setActiveEditorSlide] = useState(0);
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);

  const editorSlides = useMemo(() => {
    const list = sortedExecutiveEditors;
    if (!list || list.length === 0) return [];
    const chunkSize = 3;
    const chunks = [];
    for (let i = 0; i < list.length; i += chunkSize) {
      chunks.push(list.slice(i, i + chunkSize));
    }
    return chunks;
  }, [sortedExecutiveEditors]);

  useEffect(() => {
    if (editorSlides.length <= 1 || isCarouselHovered) return;
    const interval = setInterval(() => {
      setActiveEditorSlide((prev) => (prev + 1) % editorSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [editorSlides.length, isCarouselHovered]);

  return (
    <div className="bg-[#FAF9F6] text-gray-800 font-sans min-h-screen">

      {/* ================= 1. HERO BANNER SECTION ================= */}
      <section className="relative overflow-hidden w-full bg-[#F4F9FD] border-b border-gray-100">
        <div className="relative w-full min-h-[160px] sm:min-h-[200px] md:min-h-[240px] lg:min-h-[280px] flex items-center">
          {/* Banner Graphic Image */}
          <img
            src={ajhmBanner}
            alt="AIM Journal of Homoeopathic Medicine Banner"
            className="absolute inset-0 w-full h-full object-cover object-right sm:object-center pointer-events-none select-none"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/ajhm-banner.png';
            }}
          />

          {/* Content Over Banner */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-6 sm:py-8 md:py-10">
            <div className="max-w-2xl space-y-2 sm:space-y-2.5 bg-white/60 sm:bg-transparent backdrop-blur-xs sm:backdrop-blur-none p-3 sm:p-0 rounded-2xl">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-serif font-extrabold text-[#0B3B60] leading-tight tracking-tight">
                AIM Journal of Homoeopathic Medicine
              </h1>

              <p className="text-sm sm:text-base text-[#334E68] font-medium leading-relaxed max-w-xl">
                A Peer-Reviewed Journal of Homoeopathic Medicine and Related Healthcare Research
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 2. CURRENT ISSUE SECTION (FULL WIDTH) ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="rounded-3xl overflow-hidden shadow-sm bg-white border border-gray-100">
          {/* Header Banner */}
          <div className="bg-[#03478E] px-6 sm:px-8 py-3 flex items-center justify-between flex-wrap gap-2 text-white">
            <div className="flex items-center gap-2.5">
              <h2 className="text-sm font-bold tracking-wider uppercase text-white">
                {formatIssueRecord(currentIssue)}
              </h2>
              <span className="text-xs uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full bg-amber-400 text-[#0B3B60] shadow-sm hidden sm:inline-block">
                Current Issue
              </span>
            </div>
            <Link
              to="/current-issue"
              className="text-xs text-blue-100 hover:text-white underline font-semibold flex items-center gap-1.5"
            >
              <span>View Full Issue</span>
              <FaArrowRight className="text-[10px]" />
            </Link>
          </div>

          {/* Categorized Articles (same layout as the Current Issue page) */}
          <IssueArticleGroups articles={currentIssueJournals} emptyMessage="No articles published in the current issue yet." />
        </div>
      </section>

      {/* ================= 3. EXECUTIVE EDITORS & EDITORIAL BOARD (FULL WIDTH CAROUSEL) ================= */}
      {editors.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="rounded-3xl overflow-hidden shadow-sm bg-white border border-gray-100">
            {/* Header Banner */}
            <div className="bg-[#03478E] px-6 sm:px-8 py-3 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                {/* <FaBookReader className="text-blue-200 text-sm" /> */}
                <h3 className="text-sm font-bold tracking-wider uppercase text-white">
                  Editorial Board
                </h3>
              </div>

              <Link
                to="/editorial-board"
                className="text-xs text-blue-100 hover:text-white underline font-semibold flex items-center gap-1"
              >
                <span>View Full Board</span>
                <FaArrowRight className="text-[10px]" />
              </Link>
            </div>

            {/* Carousel Container */}
            <div
              className="p-6 sm:p-7 bg-white relative overflow-hidden"
              onMouseEnter={() => setIsCarouselHovered(true)}
              onMouseLeave={() => setIsCarouselHovered(false)}
            >
              {/* Sliding Track */}
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${activeEditorSlide * 100}%)` }}
              >
                {editorSlides.map((slideGroup, slideIdx) => (
                  <div
                    key={slideIdx}
                    className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 px-1"
                  >
                    {slideGroup.map((editor, idx) => {
                      const designation = (editor.designation || editor.title || editor.role || 'Editorial Board').trim();
                      return (
                        <div
                          key={editor.id || editor.name || idx}
                          className="bg-[#F8FAFC] hover:bg-blue-50/40 rounded-2xl p-5 border border-gray-100 shadow-2xs hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                        >
                          <div className="space-y-3.5">
                            <div className="flex items-center gap-3.5">
                              <div className="w-14 h-14 rounded-full overflow-hidden bg-blue-50 flex-shrink-0 border-2 border-blue-100 shadow-xs">
                                <img
                                  src={editor.img || '/assets/img/default-doctor.svg'}
                                  alt={editor.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/assets/img/default-doctor.svg';
                                  }}
                                />
                              </div>
                              <div className="min-w-0">
                                <h4 className="sub-heading">
                                  {editor.name}
                                </h4>
                                <span className="inline-block text-xs font-bold text-[#03478E] bg-blue-50/80 px-2 py-0.5 rounded-full mt-1 uppercase tracking-wide">
                                  {designation}
                                </span>
                              </div>
                            </div>

                            <div className="space-y-1 text-sm text-gray-600">
                              {(editor.memberDesignation || editor.qualification) && (
                                <p className="font-medium text-gray-800 flex items-start gap-1.5 leading-snug">
                                  <FaBriefcase className="text-[#03478E] text-[11px] flex-shrink-0 mt-0.5" />
                                  <span>{editor.memberDesignation || editor.qualification}</span>
                                </p>
                              )}
                              {(editor.address || editor.institution) && (
                                <p className="text-gray-500 text-xs leading-relaxed flex items-start gap-1.5">
                                  <FaMapMarkerAlt className="text-red-500 text-[11px] flex-shrink-0 mt-0.5" />
                                  <span>{editor.address || editor.institution}</span>
                                </p>
                              )}
                            </div>
                          </div>

                          {/* <div className="pt-3 mt-3 border-t border-gray-200/60 flex items-center justify-between gap-2">
                            {editor.email ? (
                              <a
                                href={`mailto:${editor.email}`}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#03478E] hover:text-[#023366] hover:underline truncate max-w-[170px]"
                                title={editor.email}
                              >
                                <FaEnvelope className="text-[10px] flex-shrink-0 text-[#03478E]" />
                                <span className="truncate">{editor.email}</span>
                              </a>
                            ) : (
                              <span className="text-xs text-gray-400">AJHM Editorial Member</span>
                            )}

                            {editor.profileLink ? (
                              <a
                                href={normalizeUrl(editor.profileLink)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-bold text-[#03478E] hover:text-[#023366] hover:underline inline-flex items-center gap-1 flex-shrink-0"
                              >
                                <span>Profile</span>
                                <FaArrowRight className="text-[8px]" />
                              </a>
                            ) : (
                              <Link
                                to="/editorial-board"
                                className="text-xs font-bold text-[#03478E] hover:text-[#023366] hover:underline inline-flex items-center gap-1 flex-shrink-0"
                              >
                                <span>Profile</span>
                                <FaArrowRight className="text-[8px]" />
                              </Link>
                            )}
                          </div> */}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Pagination Indicators */}
              {editorSlides.length > 1 && (
                <div className="flex items-center justify-center gap-2 pt-5">
                  {editorSlides.map((_, dotIdx) => (
                    <button
                      key={dotIdx}
                      type="button"
                      onClick={() => setActiveEditorSlide(dotIdx)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        activeEditorSlide === dotIdx
                          ? 'w-7 bg-[#03478E]'
                          : 'w-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to slide ${dotIdx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ================= 4. THREE PILLAR FEATURE GRID (ABOUT, AIMS & SCOPE, PUBLISHER) ================= */}
      <section className="py-12 sm:py-14 w-full bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">

            {/* Pillar 1: About AJHM */}
            <div className="p-7 bg-[#F8FAFC] hover:bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between border border-gray-100">
              <div>
                {/* <span className="text-xs font-bold uppercase tracking-wider text-[#03478E] bg-blue-50 px-2.5 py-1 rounded-full inline-block mb-3">
                  Overview
                </span> */}
                <h4 className="sub-heading mb-2.5">
                  {cards?.card1_heading}
                </h4>
                <div className={CARD_BODY_CLASS} dangerouslySetInnerHTML={{ __html: cards?.card1_body || '' }} />
              </div>
              <Link
                to="/about-us"
                className="text-xs font-bold text-[#03478E] hover:text-[#023366] hover:underline inline-flex items-center gap-1.5 mt-5"
              >
                <span>Learn More About Journal</span>
                <FaArrowRight className="text-[10px]" />
              </Link>
            </div>

            {/* Pillar 2: Aims & Scope */}
            <div className="p-7 bg-[#F8FAFC] hover:bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between border border-gray-100">
              <div>
                {/* <span className="text-xs font-bold uppercase tracking-wider text-[#03478E] bg-blue-50 px-2.5 py-1 rounded-full inline-block mb-3">
                  Focus & Scope
                </span> */}
                <h4 className="sub-heading mb-2.5">
                  {cards?.card2_heading}
                </h4>
                <div className={CARD_BODY_CLASS} dangerouslySetInnerHTML={{ __html: cards?.card2_body || '' }} />
              </div>
              <Link
                to="/aims-and-scope"
                className="text-xs font-bold text-[#03478E] hover:text-[#023366] hover:underline inline-flex items-center gap-1.5 mt-5"
              >
                <span>Explore Full Aims & Scope</span>
                <FaArrowRight className="text-[10px]" />
              </Link>
            </div>

            {/* Pillar 3: Publisher */}
            <div className="p-7 bg-[#F8FAFC] hover:bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between border border-gray-100">
              <div>
                {/* <span className="text-xs font-bold uppercase tracking-wider text-[#03478E] bg-blue-50 px-2.5 py-1 rounded-full inline-block mb-3">
                  Publisher
                </span> */}
                <h4 className="sub-heading mb-2.5">
                  {cards?.card3_heading}
                </h4>
                <div className={CARD_BODY_CLASS} dangerouslySetInnerHTML={{ __html: cards?.card3_body || '' }} />
              </div>
              <div className="mt-5 pt-3 border-t border-gray-200/60 flex items-center justify-between">
                <Link
                  to="/about-us#publisher-info"
                  className="text-xs font-bold text-[#03478E] hover:text-[#023366] hover:underline inline-flex items-center gap-1.5"
                >
                  <span>About Publisher</span>
                  <FaArrowRight className="text-[10px]" />
                </Link>
                {/* <span className="flex flex-col items-end gap-0.5">
                  <a
                    href="mailto:editor@aimjhm.com"
                    className="text-xs text-[#03478E] hover:underline font-medium"
                  >
                    editor@aimjhm.com
                  </a>
                  <a
                    href="mailto:domus.publications@gmail.com"
                    className="text-xs text-[#03478E] hover:underline font-medium"
                  >
                    domus.publications@gmail.com
                  </a>
                </span> */}
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;